import { Portal } from '@reach/portal';
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useRef } from 'react';

import { CloseOutlined as Cross } from '@ant-design/icons';

import FocusTrap from '~components/common/FocusTrap';
import StopPropagation from '~components/common/StopPropagation';

import s from './Modal.module.css';

export interface ModalProps {
  title?: string;
  className?: string;
  children?: any;
  open?: boolean;
  onClose?: () => void;
  onEnter?: () => void | null;
  preventClose?: boolean;
}

const Modal: FC<ModalProps> = ({ children, open, onClose, className, preventClose }) => {
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventClose && onClose) {
        return onClose();
      }
    },
    [onClose, preventClose],
  );
  useEffect(() => {
    setTimeout(() => {
      if (ref.current) {
        if (open) {
          disableBodyScroll(ref.current);
          window.addEventListener('keydown', handleKey);
        } else {
          enableBodyScroll(ref.current);
        }
      }
    }, 100);
    return () => {
      window.removeEventListener('keydown', handleKey);
      clearAllBodyScrollLocks();
    };
  }, [open, handleKey]);

  return (
    <Portal>
      <div
        className={classNames(s.root, { [s.open]: open }, className)}
        onClick={() => {
          if (!preventClose && onClose) onClose();
        }}
        ref={ref}
      >
        <div className={s.modal}>
          <StopPropagation>
            {open && (
              <div className={s.content}>
                <FocusTrap focusFirst>{children}</FocusTrap>
                {!preventClose && (
                  <button onClick={() => onClose?.()} aria-label="Close modal" className={s.close}>
                    <Cross />
                  </button>
                )}
              </div>
            )}
          </StopPropagation>
        </div>
      </div>
    </Portal>
  );
};

export type ModalType = typeof Modal;

export default Modal;
